import { gql } from '@apollo/client'
import { Modal, VisuallyHidden } from '@farol-ds/react'
import { useCallback, useEffect, useState } from 'react'

import { IdDocumentValidation_ViewerFragment } from './__generated__/id-document-validation.graphql'
import { IdDocumentValidationContextProvider } from './context'
import { Steps } from './enums'
import styles from './id-document-validation.module.scss'
import { StepRenderer } from './step-renderer'
import { IdDocumentValidationEventData, IdDocumentValidationTexts } from './types'

interface IdDocumentValidationProps {
  viewer: IdDocumentValidation_ViewerFragment | null
  isOpen: boolean
  eventData: IdDocumentValidationEventData
  validateCallback?: (isValidatedSuccess: boolean) => void
  texts?: IdDocumentValidationTexts
  onOpenChange?: (isOpen: boolean) => void
  validationOrigin: string
  onAfterValidationSuccess?: () => void | Promise<void>
}

function hasRequiredViewerData(viewer: IdDocumentValidation_ViewerFragment | null): boolean {
  return Boolean(viewer?.cpf && viewer?.birthDate)
}

export function IdDocumentValidation(props: IdDocumentValidationProps) {
  const {
    texts,
    isOpen,
    eventData,
    viewer,
    validateCallback,
    onOpenChange,
    validationOrigin,
    onAfterValidationSuccess,
  } = props

  const [currentStep, setCurrentStep] = useState<Steps>(Steps.VALIDATE_DOCUMENT)

  const onClose = useCallback(() => {
    onOpenChange?.(false)
  }, [onOpenChange])

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(Steps.VALIDATE_DOCUMENT)
    }
  }, [isOpen])

  if (!isOpen || !viewer || !hasRequiredViewerData(viewer)) {
    return null
  }

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <Modal.Header>
          <Modal.HeaderTitle>Document Validation</Modal.HeaderTitle>
        </Modal.Header>
      </VisuallyHidden>
      <Modal.Content className={styles.modalContent}>
        <IdDocumentValidationContextProvider
          setCurrentStep={setCurrentStep}
          eventData={eventData}
          viewer={viewer}
          validationOrigin={validationOrigin}
          texts={texts}
          onAfterValidationSuccess={onAfterValidationSuccess}
        >
          <Modal.IconClose onClick={onClose} data-testid="close-button" />
          <StepRenderer
            onClose={onClose}
            currentStep={currentStep}
            stepsCallback={validateCallback}
          />
        </IdDocumentValidationContextProvider>
      </Modal.Content>
    </Modal>
  )
}

IdDocumentValidation.fragments = {
  viewer: gql`
    fragment IdDocumentValidation_viewer on Profile {
      pid
      cpf
      birthDate
    }
  `,
}
